import {
  isRuntimeAdmittedLibraryRagOrchestrationSuccess,
  type LibraryRagOrchestrationResult,
} from '@cogo/agentic-generator/library-rag-agent-orchestration'
import { hashCanonicalContent } from '@cogo/schema/canonical-content-hash'

type SuccessfulLibraryRagResolution = Extract<
  LibraryRagOrchestrationResult,
  { ok: true }
>

export type StudioLibraryRagRuntimeAuthority =
  | 'admitted-platform'
  | 'admitted-legacy'
  | 'untrusted'

const MAX_ADMITTED_RUNTIME_RESOLUTIONS = 128
const admittedRuntimeResolutions = new Map<
  `sha256:${string}`,
  'platform' | 'legacy'
>()

/**
 * Coordinator-only transport boundary. The coordinator calls this only after
 * both authenticated same-origin requests have completed and the compiler has
 * bound their parsed receipts to one exact result. Only canonical hashes are
 * retained, so a normal structuredClone keeps the same authority while any
 * envelope mutation fails closed.
 */
export function admitAuthenticatedStudioLibraryRagResolution(
  resolution: SuccessfulLibraryRagResolution,
): SuccessfulLibraryRagResolution {
  const snapshot = structuredClone(resolution)
  if (!hasPlatformReceiptPair(snapshot)) {
    throw new Error('studio-library-rag-platform-authority-incomplete')
  }
  admitExactResolution(snapshot, 'platform')
  return structuredClone(snapshot)
}

/**
 * Assistant-internal legacy boundary. Callers must first prove that the exact
 * object came from the generator's private in-process success factory. A
 * reconstructed or persisted result is not eligible for this registration.
 */
export function admitTrustedLegacyStudioLibraryRagResolution(
  resolution: SuccessfulLibraryRagResolution,
): SuccessfulLibraryRagResolution {
  if (!isRuntimeAdmittedLibraryRagOrchestrationSuccess(resolution)) {
    throw new Error('studio-library-rag-legacy-authority-untrusted')
  }
  const snapshot = structuredClone(resolution)
  if (hasAnyPlatformReceipt(snapshot)) {
    throw new Error('studio-library-rag-legacy-authority-invalid')
  }
  admitExactResolution(snapshot, 'legacy')
  return structuredClone(snapshot)
}

export function classifyStudioLibraryRagRuntimeAuthority(
  resolution: SuccessfulLibraryRagResolution,
): StudioLibraryRagRuntimeAuthority {
  try {
    const fullResolutionHash = hashCanonicalContent(resolution)
    const admitted = admittedRuntimeResolutions.get(fullResolutionHash)
    if (admitted === undefined) return 'untrusted'
    admittedRuntimeResolutions.delete(fullResolutionHash)
    admittedRuntimeResolutions.set(fullResolutionHash, admitted)
    if (admitted === 'platform') {
      return hasPlatformReceiptPair(resolution)
        ? 'admitted-platform'
        : 'untrusted'
    }
    return hasAnyPlatformReceipt(resolution)
      ? 'untrusted'
      : 'admitted-legacy'
  } catch {
    return 'untrusted'
  }
}

function admitExactResolution(
  resolution: SuccessfulLibraryRagResolution,
  authority: 'platform' | 'legacy',
): void {
  const fullResolutionHash = hashCanonicalContent(resolution)
  admittedRuntimeResolutions.delete(fullResolutionHash)
  admittedRuntimeResolutions.set(fullResolutionHash, authority)
  while (admittedRuntimeResolutions.size > MAX_ADMITTED_RUNTIME_RESOLUTIONS) {
    const oldest = admittedRuntimeResolutions.keys().next().value
    if (oldest === undefined) break
    admittedRuntimeResolutions.delete(oldest)
  }
}

function hasAnyPlatformReceipt(
  resolution: SuccessfulLibraryRagResolution,
): boolean {
  return (
    resolution.evidence.planner.authorityReceipt !== undefined ||
    resolution.evidence.critic.authorityReceipt !== undefined
  )
}

function hasPlatformReceiptPair(
  resolution: SuccessfulLibraryRagResolution,
): boolean {
  return (
    resolution.evidence.planner.authorityReceipt !== undefined &&
    resolution.evidence.critic.authorityReceipt !== undefined
  )
}
